/**
 * MOCK/TEMPORARY — the generations table, standing in for the one the server will own.
 *
 * ============================================================================
 * WHY THIS FILE HAD TO EXIST, and it is Batch 7 §1.7 in one sentence: `createGeneration`
 * collapsed `POST /generations` and `GET /generations/:id` into a single promise that
 * resolved with a FINISHED SONG after 1100ms, so the frontend had exactly one shape for
 * generation — "a request that returns the track" — and no way to represent a job that had
 * been accepted and not yet finished.
 *
 * That shape is only implementable by a backend that holds the connection open until the
 * audio exists. Music generation takes minutes, which is why the contract has always
 * documented an accept-then-poll flow (docs/api-contract.md §8) and why `GenerationJob`
 * has been in types/domain since the beginning. What was missing was anything that USED
 * it: the type had no runtime references at all, only doc comments pointing at it.
 *
 * So the mock owns a jobs table, the way a server owns a jobs table, and the two documented
 * endpoints query it:
 *
 *   generationApi.createGenerationJob(input)  → POST /generations   (202, job queued)
 *   generationApi.getGeneration(id)           → GET  /generations/:id
 *
 * The client-visible protocol is now the real one: a caller gets a job id back, polls until
 * `succeeded` or `failed`, and only then does a song exist to fetch. Nothing can mark a
 * generation ready merely because the request was accepted, because acceptance and
 * completion are two different rows-in-time on the same record.
 * ============================================================================
 *
 * WHAT IS SIMULATED, PRECISELY. The COMPLETION is. `mockGenerationEnqueue` is handed the
 * song the job will eventually produce, and withholds it until the job reaches `succeeded`
 * — which is exactly what the 1100ms timer did before, so the simulation is no more and no
 * less honest than it was. What changed is the protocol around it.
 *
 * The song is passed in rather than built here for two reasons. It keeps the fabrication
 * (lyrics, cover concept, random BPM) in generationApi where it is documented, so this is a
 * new seam rather than a move of load-bearing code; and generationApi already imports this
 * module, so building the song here would need an import back the other way.
 *
 * NO PROGRESS IS INVENTED. `progress` is `0` until the job completes and `100` when it
 * does. There is no signal to interpolate from — a number creeping upwards on a timer is
 * the "reports elapsed time as work completed" defect that the Genre Mixer's four ticking
 * rows were deleted for (see generationApi.SIMULATED_TIMINGS). A real backend reporting
 * genuine progress is what turns the UI's progress bar on; see components/studio/
 * ProcessingWave.
 *
 * NOTHING FAILS ON ITS OWN. `mockGenerationFailNext` is the only way to reach the failed
 * branch, and it is for tests. A mock that failed one generation in ten would be inventing
 * an error the provider did not report, and would make every generation test flaky.
 *
 * DELETED WHOLE when the backend lands. Nothing outside src/api/ may import it except
 * `mockGenerationsReset`, which src/test/setup.ts calls for the reason mockCatalogue's
 * reset exists.
 */
import type { GenerationJob, Song } from '../types/domain';
import { mockCatalogueUpsert } from './mockCatalogue';

/**
 * When the simulated job changes state, in ms from the request arriving.
 *
 * THE SERVER'S CLOCK, not the client's — which is why these live here and the request
 * latencies live in generationApi.GENERATION_JOB_TIMINGS. A poll observes whatever stage
 * the job has reached by the time it lands, exactly as it would against a real queue, and
 * the client is not told in advance how long anything will take.
 *
 * Both figures are drawn from the prototype's original 1100ms generation so the perceived
 * pacing is unchanged: `finished` falls between the client's second and third poll, so a
 * generation settles on the third — which is what makes generationApi's
 * SIMULATED_GENERATION_POLLS the real count rather than a figure chosen to make the
 * arithmetic come out. Both halves of that are pinned by
 * api/__tests__/generationJob.test.ts, because a change to either table's numbers can
 * silently move which poll lands first.
 *
 * In practice nothing on screen moves at these speeds anyway: every creation path holds its
 * processing state for PROCESSING_MS (lib/processing.ts), which is longer than the whole job
 * and therefore always the wait that is actually seen.
 */
export const MOCK_GENERATION_STAGES = {
  /** queued → running. Before the first poll lands, so `running` is observable. */
  running: 400,
  /** running → succeeded (or → failed, when a test has asked for one). */
  finished: 900,
} as const;

/** A job and the song it will produce, which no reader may see before `succeeded`. */
interface Row {
  job: GenerationJob;
  /** Withheld until the job succeeds, then written into the songs table. */
  song: Song;
}

let rows = new Map<string, Row>();

/**
 * Pending stage transitions, tracked so `reset()` can cancel them.
 *
 * WITHOUT THIS, A TEST LEAKS A TIMER. A test that starts a generation and asserts on the
 * queued job returns while two `setTimeout`s are still scheduled; they fire during whatever
 * test runs next and upsert a song into the songs table (see mockCatalogue's own note on
 * why that is the quietest kind of wrong a fixture can be).
 */
let timers: ReturnType<typeof setTimeout>[] = [];

/** Set by `mockGenerationFailNext`, consumed by the next enqueue. Tests only. */
let failNextWith: string | null = null;

/** A detached copy, so a caller mutating what it got cannot reach into the table. */
function copyJob(job: GenerationJob): GenerationJob {
  return { ...job };
}

/** Schedules a stage transition, recording the timer so a reset can cancel it. */
function at(ms: number, change: () => void): void {
  timers.push(setTimeout(change, ms));
}

/** Replaces a row's job, if the row still exists. A reset between stages removes it. */
function advance(id: string, patch: Partial<GenerationJob>): void {
  const row = rows.get(id);
  if (!row) return;
  rows.set(id, { ...row, job: { ...row.job, ...patch } });
}

/**
 * Records an accepted generation and starts its (simulated) progress through the queue.
 *
 * `job` arrives `queued` with `progress: 0` and NO `songId`, which is the whole point: the
 * id of a song that does not exist yet cannot be handed to the client, so a caller has
 * nothing to mistake for a finished track.
 *
 * `song` is the record the job will produce. It is held here, out of reach of every read,
 * until the job succeeds — at which point it is written into the songs table
 * (`mockCatalogueUpsert`) and `songId` appears on the job. A pending generation therefore
 * puts nothing in the library and nothing in the catalogue, which is what makes
 * "generation finished" and "a song exists" the same event rather than two guesses.
 */
export function mockGenerationEnqueue(job: GenerationJob, song: Song): void {
  rows.set(job.id, { job: copyJob(job), song: { ...song } });

  at(MOCK_GENERATION_STAGES.running, () => advance(job.id, { status: 'running' }));

  /*
    The failure branch is taken only when a test has asked for it — see the header. It
    consumes the flag here rather than at the transition so that one call to
    `mockGenerationFailNext` affects exactly one job, however many are started afterwards.
  */
  const error = failNextWith;
  failNextWith = null;

  at(MOCK_GENERATION_STAGES.finished, () => {
    if (error !== null) {
      /*
        NO SONG IS WRITTEN, and `songId` stays absent. A failed job that left a row in the
        songs table would put a track in the user's library that nothing ever generated —
        the exact fabrication §1.7 rules out. `progress` stays at 0 for the same reason a
        succeeded job's becomes 100: it reports what happened, not how far a bar got.
      */
      advance(job.id, { status: 'failed', error });
      return;
    }
    const row = rows.get(job.id);
    if (!row) return;
    mockCatalogueUpsert(row.song);
    advance(job.id, { status: 'succeeded', progress: 100, songId: row.song.id });
  });
}

/** One job by id, or null. Detached, like every other read in the mock. */
export function mockGeneration(id: string): GenerationJob | null {
  const row = rows.get(id);
  return row ? copyJob(row.job) : null;
}

/**
 * Makes the NEXT generation fail instead of succeeding. TESTS ONLY.
 *
 * The only route to the failed branch, because nothing here fails by itself. It exists so
 * the poll loop's failure path — the one that surfaces "the provider could not make this
 * track" to the user — is exercised by a test rather than described by a comment.
 *
 * Deliberately not "fail the job with this id": a caller cannot know the id before
 * `createGeneration` has minted one, and the point of the test is to go through the funnel
 * the app itself uses rather than to reach into the table mid-flight.
 */
export function mockGenerationFailNext(error: string): void {
  failNextWith = error;
}

/**
 * Empties the table and cancels every pending transition. Called before every test.
 *
 * Both halves are needed: the rows leak the same way mockCatalogue's do, and the timers
 * leak worse — see `timers` above.
 */
export function mockGenerationsReset(): void {
  for (const timer of timers) clearTimeout(timer);
  timers = [];
  rows = new Map();
  failNextWith = null;
}
